// This file is part of InvenioRDM
// Copyright (C) 2023 CERN.
//
// Invenio App RDM is free software; you can redistribute it and/or modify it
// under the terms of the MIT License; see LICENSE file for more details.

/**
 * Add here all the overridden components of your app.
 */

// Import React for JSX syntax (this is REQUIRED for <a> tags etc.)
import React from "react";

// Import core InvenioRDM modal components
import { PublishModal, SubmitReviewModal } from "@js/invenio_rdm_records";

// Import the "parametrize" function that allows component overriding
import { parametrize } from "react-overridable";

/* Optional: Debugging output (only for development)
console.log("[fdm-dhbw] mapping.js loaded");
*/

// ---------------------------------------------------------------------------
// Define the extra checkbox that will appear in both modals
// ---------------------------------------------------------------------------

// NOTE:
// Instead of a plain text string, we use JSX here so that HTML elements
// (like <a href="...">) are properly rendered as clickable links.
// The page "/data-submission-agreement" must exist in invenio-pages.
const DHBWExtraCheckbox = () => (
  <>
    Ich bestätige, dass dieser Datensatz den Nutzungsbedingungen des fdm@DHBW-Dienstes
    und der{" "}
    <a
      href="/duev"
      target="_blank"
      rel="noopener noreferrer"
    >
      Datenübermittlungsvereinbarung
    </a>{" "}
    entspricht.
  </>
);

// ---------------------------------------------------------------------------
// Attach the extra checkbox to both modals
// ---------------------------------------------------------------------------

// The "parameters" object defines which new fields should be injected
// into the PublishModal and SubmitReviewModal components.
// "extraCheckboxes" is the official hook for adding new compliance checkboxes.
const parameters = {
  extraCheckboxes: [
    {
      // fieldPath: internal name used for validation and serialization
      fieldPath: "acceptTermsOfService",

      // text: the content displayed next to the checkbox
      // IMPORTANT: here we render our React component instead of a plain string
      text: <DHBWExtraCheckbox />,
    },
  ],
};

// ---------------------------------------------------------------------------
// Create parametrized component versions
// ---------------------------------------------------------------------------

// Each modal (Publish + SubmitReview) receives the same extra checkbox.
// "parametrize" creates a new component variant based on the original.
const PublishModalComponent = parametrize(PublishModal, parameters);
const SubmitReviewModalComponent = parametrize(SubmitReviewModal, parameters);

// ---------------------------------------------------------------------------
// Export overridden components
// ---------------------------------------------------------------------------

// These exports tell InvenioRDM to use your customized versions
// instead of the default ones when rendering the modals.
export const overriddenComponents = {
  "InvenioRdmRecords.PublishModal.container": PublishModalComponent,
  "InvenioRdmRecords.SubmitReviewModal.container": SubmitReviewModalComponent,
};

// ---------------------------------------------------------------------------
// Notes:
// ---------------------------------------------------------------------------
// - Make sure you have added the Data Submission Agreement page in your system:
//       invenio pages create /data-submission-agreement "Data Submission Agreement" \
//           --content "<h1>Data Submission Agreement</h1><p>...</p>"
// - After saving this file, rebuild the JS bundle:
//       invenio-cli assets build
// - Then restart your web container if you are running InvenioRDM via Docker.
// ===========================================================================